import {
  Component,
  computed,
  ElementRef,
  HostBinding,
  input,
  OnInit,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, FormsModule } from '@angular/forms';
import {
  CursorPositionUpdateFactory,
  FmntsMaskedInputModule,
  UpdateCursorFn,
  ValueTransform,
} from '@fmnts/components/masked-input';
import { isNonEmptyString, isString } from '@fmnts/core';
import {
  AbstractDeclarativeInputFieldComponent,
  ErrorMessagePipe,
  FormTextInputField,
} from '@fmnts/declarative-forms';
import { I18nModule } from '@fmnts/i18n';
import { Bic, Iban, SortCode } from '@formunauts/banking/domain';
import { declarativeFormFieldsetsWithIban } from '@formunauts/shared/declarative-forms/domain';
import * as Fn from 'effect/Function';
import * as O from 'effect/Option';

/**
 * Component that renders an input field.
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'next-text-field',
  templateUrl: './text-field.component.html',
  imports: [FmntsMaskedInputModule, FormsModule, ErrorMessagePipe, I18nModule],
})
export class TextFieldComponent
  extends AbstractDeclarativeInputFieldComponent<
    FormTextInputField,
    string | null
  >
  implements ControlValueAccessor, OnInit
{
  @HostBinding('class') componentClass = 'form-field form-field__text';

  /**
   * Value for the `autocomplete` attribute of the input field.
   */
  readonly autocomplete = input(this._getAutocompleteBehavior());

  protected _displayValueTransformer: ValueTransform | undefined = undefined;
  private _value: string | null = '';

  readonly inputWithTransformer = viewChild<ElementRef<HTMLInputElement>>(
    'inputWithTransformer',
  );

  readonly inputWithoutTransformer = viewChild<ElementRef<HTMLInputElement>>(
    'inputWithoutTransformer',
  );

  readonly #input = computed(() =>
    Fn.pipe(
      this._displayValueTransformer
        ? this.inputWithTransformer()
        : this.inputWithoutTransformer(),
      O.fromNullable,
      O.getOrThrow,
    ),
  );

  ngOnInit(): void {
    // TODO: Pass transformers as @Input from generic-field.component
    if (declarativeFormFieldsetsWithIban.includes(this.fieldset.key)) {
      if (this.field.key === 'account_bic_or_bankcode') {
        this._displayValueTransformer = {
          transform: (value) =>
            new Bic.LegacyBic(value || '').value.toUpperCase(),
          reverse: (displayValue) =>
            new Bic.LegacyBic(displayValue || '').value.toUpperCase(),
        };
      }

      if (this.field.key === 'account_iban_or_number') {
        this._displayValueTransformer = {
          transform: (value) =>
            new Iban.LegacyIban(value || '').format().toUpperCase(),
          reverse: (displayValue: string) =>
            Iban.LegacyIban.parse(displayValue || '').value.toUpperCase(),
          updateCursor: CursorPositionUpdateFactory.fixedGroups(
            1,
            4,
          ) as UpdateCursorFn<string, string | null | undefined>,
        };
      }
    }

    if (this.fieldset.key === 'sortCodesBankData') {
      if (this.field.key === 'account_bic_or_bankcode') {
        this._displayValueTransformer = {
          transform: (value) =>
            new SortCode.LegacySortCode(value || '').format().toUpperCase(),
          reverse: (displayValue) =>
            SortCode.LegacySortCode.parse(
              displayValue || '',
            ).value.toUpperCase(),
          updateCursor: CursorPositionUpdateFactory.fixedGroups(
            1,
            2,
          ) as UpdateCursorFn<string, string | null | undefined>,
        };
      }
    }

    if (this.field.widgetOptions.format === 'alphanumeric') {
      this._displayValueTransformer = {
        transform: (v) => v || '',
        reverse: (displayValue) =>
          typeof displayValue === 'string'
            ? displayValue.replace(/[^a-zA-Z0-9]/g, '')
            : displayValue,
      };
    }

    if (this.field.widgetOptions.format === 'alphabetical') {
      this._displayValueTransformer = {
        transform: (v) => v || '',
        reverse: (displayValue) =>
          typeof displayValue === 'string'
            ? displayValue.replace(/[^a-zA-Z]/g, '')
            : displayValue,
      };
    }
  }

  /**
   * Trims white space before and after the value
   * Used on first and last name to improve data quality
   */
  public trim(): void {
    if (this._value === null) {
      return;
    }

    if (this.fieldset.key === 'personalDataKeys') {
      if (this.field.key === 'first_name' || this.field.key === 'last_name') {
        this.value = this._value.trim();
      }
    }
  }

  get value(): string | null {
    return this._value;
  }

  set value(value: string | null) {
    this.changeValue(value);
    this._onTouched();
  }

  /**
   * Changes the value and calls the onChange listeners.
   *
   * @param value The new value
   */
  private changeValue(value: string | null) {
    this._value = value;
    this._onChange(value);
  }

  writeValue(value: unknown): void {
    // Set value to given string or null. It might be changed afterwards in case that
    // a reset was detected.
    this._value = isString(value) ? value : null;
  }

  get inputMode(): string {
    const keyboardType = this.field.widgetOptions.keyboardType ?? '';
    return ['email', 'number', 'tel'].includes(keyboardType)
      ? keyboardType
      : 'text';
  }

  get inputType(): string {
    const keyboardType = this.field.widgetOptions.keyboardType ?? '';
    if (this.field.widgetOptions.format === 'date') {
      return 'date';
    }

    return ['email', 'number', 'tel'].includes(keyboardType)
      ? keyboardType
      : 'text';
  }

  get autocapitalize(): string {
    return this.field.widgetOptions.autocapitalize ?? '';
  }

  get showError(): boolean {
    return (
      this.form &&
      (isNonEmptyString(this.value) || this.control.touched) &&
      this.control.invalid
    );
  }

  protected override setFocusedState(isFocused: boolean): void {
    const inputElement = this.#input().nativeElement;
    if (isFocused) {
      inputElement.focus();
    } else {
      inputElement.blur();
    }
  }
}
